const DEFAULT_LOCALE = "en-GB";

// RTL detection mirrors @zzish/tardis isRightToLeft + borough's languageIsRTL helper
// (ar, he, fa, ur, ps, ckb, sd, and Shahmukhi Punjabi pa-Arab*), kept in sync across repos.
// Deliberately LTR: Gurmukhi Punjabi (pa / pa-IN) and Kurmanji Kurdish (ku, Latin script).
const RTL_BASE_LANGUAGES = new Set(["ar", "he", "fa", "ur", "ps", "ckb", "sd"]);

export const languageIsRTL = (locale: string): boolean => {
    if (!locale || typeof locale !== "string") return false;
    const normalized = locale.toLowerCase();
    // Punjabi splits by script: Shahmukhi (Arabic) is RTL, Gurmukhi is LTR.
    if (normalized.startsWith("pa-arab")) return true;
    if (normalized === "pa" || normalized.startsWith("pa-")) return false;
    const base = normalized.split("-")[0];
    return RTL_BASE_LANGUAGES.has(base);
};

export const normaliseLocale = (value: string | null) => {
    if (!value) {
        return DEFAULT_LOCALE;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return DEFAULT_LOCALE;
    }

    if (/^[a-z]{2}$/i.test(trimmed)) {
        return trimmed.toLowerCase() === "en" ? DEFAULT_LOCALE : trimmed;
    }

    if (/^[a-z]{2,3}[-_][a-z0-9]{2,8}$/i.test(trimmed)) {
        const [language, region] = trimmed.replace("_", "-").split("-");
        return `${language.toLowerCase()}-${region.toUpperCase()}`;
    }

    return DEFAULT_LOCALE;
};

export const getInitialLocale = () => {
    const params = new URLSearchParams(window.location.search);
    const requestedLocale =
        params.get("language") ||
        params.get("locale") ||
        window.localStorage.getItem("language");

    return normaliseLocale(requestedLocale);
};

export const applyDocumentLocale = (locale: string) => {
    document.documentElement.lang = locale;
    document.documentElement.dir = languageIsRTL(locale) ? "rtl" : "ltr";
    window.localStorage.setItem("language", locale);
};

export const withLocaleParam = (url: string, locale: string) => {
    try {
        const parsedUrl = new URL(url, window.location.origin);
        parsedUrl.searchParams.set("language", locale);
        return parsedUrl.toString();
    } catch {
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}language=${encodeURIComponent(locale)}`;
    }
};
